import { TextInput, TextInputProps, View } from 'react-native'
import React, {
  forwardRef,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react'
import { useSharedValue, withSpring } from 'react-native-reanimated'
import { colors } from '@ui/colors'
import InputContainer from './InputContainer'
import { InputProps } from './types'
import cn from '@ui/utils/cn'
import InputTextEnd from './InputTextEnd'
import { MASK_RESERVER_CHAR, INPUT_MASK } from '@ui/utils/masks'
import { CLEAN_INPUT_REGEX, formatWithMask } from './utils'
import InputLabelTop from './InputLabelTop'

type MaskValue = (typeof INPUT_MASK)[keyof typeof INPUT_MASK]
const Input = forwardRef<TextInput, InputProps & { mask?: MaskValue }>(
  (
    {
      label,
      labelVariant = 'inline',
      labelWidth,
      placeholder,
      icon,
      required,
      onFocus,
      onBlur,
      className,
      center,
      style,
      errorMessage,
      successMessage,
      isSuccess,
      action,
      textEnd,
      hideIndicator,
      /*
       * The mask can cause issues with multichar characters like emojis or some specific foreign languages
       */
      mask,
      ...rest
    },
    ref
  ) => {
    const success = useSharedValue(0)
    const error = useSharedValue(0)
    const focus = useSharedValue(0)
    const disabled = useSharedValue(0)
    const readonly = useSharedValue(0)
    const localRef = useRef<TextInput | null>(null)

    const [internalValue, setInternalValue] = useState<string>('')
    const prevMaskedValue = useRef('')
    const prevRawValue = useRef('')
    const isInputDisabled = !!(rest.disabled || rest.readonly)

    const onChangeText = rest?.onChangeText

    const handleChangeText = useCallback(
      (text: string) => {
        if (mask) {
          // Remove all mask characters from the input, keep only valid input for X
          const raw = text.replace(CLEAN_INPUT_REGEX, '')
          // Detect backspace over mask character
          if (
            prevMaskedValue.current.length > text.length && // user deleted
            prevRawValue.current.length === raw.length // but raw didn't change
          ) {
            // Remove last character from raw value
            const newRaw = raw.slice(0, -1)
            setInternalValue(newRaw)
            onChangeText?.(newRaw)
            prevMaskedValue.current = formatWithMask(
              newRaw,
              mask,
              MASK_RESERVER_CHAR
            )
            prevRawValue.current = newRaw
            return
          }
          setInternalValue(raw)
          onChangeText?.(raw)
          prevMaskedValue.current = text
          prevRawValue.current = raw
        } else {
          setInternalValue(text)
          onChangeText?.(text)
        }
      },
      [mask, onChangeText]
    )

    const displayValue =
      mask && internalValue.length > 0
        ? formatWithMask(internalValue, mask, MASK_RESERVER_CHAR)
        : ''

    const setRefs = useCallback(
      (node: TextInput | undefined | null) => {
        if (!node) return
        // In case ref is a function we need to also set the local ref
        // to have access to it
        localRef.current = node
        if (typeof ref === 'function') {
          ref(node)
        }
      },
      [ref]
    )

    const onLabelPress = useCallback(() => {
      if (isInputDisabled) return
      if (ref && 'current' in ref) ref.current?.focus()
      localRef.current?.focus()
    }, [isInputDisabled, ref])

    const handleBlur = useCallback<NonNullable<TextInputProps['onBlur']>>(
      (e) => {
        focus.set(
          withSpring(0, {
            overshootClamping: true,
          })
        )
        onBlur?.(e)
      },
      [onBlur, focus]
    )

    const handleFocus = useCallback<NonNullable<TextInputProps['onFocus']>>(
      (e) => {
        focus.set(
          withSpring(1, {
            overshootClamping: true,
          })
        )
        onFocus?.(e)
      },
      [onFocus, focus]
    )

    useEffect(() => {
      success.set(withSpring(successMessage || isSuccess ? 1 : 0))
      error.set(withSpring(errorMessage ? 1 : 0))
      disabled.set(withSpring(rest.disabled ? 1 : 0))
      readonly.set(withSpring(rest.readonly ? 1 : 0))
    }, [
      success,
      successMessage,
      isSuccess,
      error,
      errorMessage,
      disabled,
      readonly,
      rest.disabled,
      rest.readonly,
    ])

    const inputContainer = (
      <InputContainer
        readonly={readonly}
        hideIndicator={hideIndicator}
        className={cn('relative', className)}
        style={style}
        onLabelPress={onLabelPress}
        label={label}
        labelWidth={labelWidth}
        labelVariant={labelVariant}
        icon={icon}
        success={success}
        error={error}
        focus={focus}
        disabled={disabled}
        successMessage={successMessage}
        errorMessage={errorMessage}
        required={required}
        center={center}
      >
        <TextInput
          ref={typeof ref === 'function' ? setRefs : ref ?? localRef}
          inputMode="text"
          tabIndex={0}
          selectionColor={colors.text}
          onFocus={handleFocus}
          placeholder={placeholder}
          onBlur={handleBlur}
          autoComplete="off"
          hitSlop={{
            top: 10,
            bottom: 10,
            left: 10,
            right: 10,
          }}
          {...rest}
          editable={rest.editable ?? !isInputDisabled}
          value={mask ? displayValue : rest.value}
          onChangeText={handleChangeText}
          className={cn(
            'h-full w-full flex-1 px-6 py-4 font-text-semibold tracking-[-0.28] outline-none placeholder:color-black/25',
            center && 'px-0 text-center'
          )}
          style={{
            color: rest.disabled ? colors.black25 : colors.black,
          }}
        />
        {textEnd && <InputTextEnd text={textEnd} />}
        {action}
      </InputContainer>
    )

    if (labelVariant !== 'top') {
      return inputContainer
    }

    return (
      <View className="flex flex-col gap-3">
        <InputLabelTop
          label={label}
          required={required}
          success={success}
          error={error}
          disabled={disabled}
          focus={focus}
          readonly={readonly}
        />
        {inputContainer}
      </View>
    )
  }
)

Input.displayName = 'Input'

export default Input
